import { FC } from "react";
import { faTimelineArrow } from "@fortawesome/pro-light-svg-icons";
import {
  faCircleUser,
  faCode,
  faRobot,
  faTicket,
  faToolbox,
} from "@fortawesome/pro-solid-svg-icons";
import { Group, MantineSize, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

import { ScmType } from "@protos/common/v1/common";
import { Reachability, Transitivity } from "@protos/sca/v1/sca";
import { HistoricalInfo, ValidationState } from "@protos/secrets/v1/secrets";
import { ExternalTicket, TicketAttempt } from "@protos/ticketing/v1/ticketing";

import { AiTagIcon } from "./AiTagIcon";
import { BranchIcon } from "./BranchIcon";
import { CodeLocationIcon } from "./CodeLocationIcon";
import { ReachabilityIcon } from "./ReachabilityIcon";
import { RepoIcon } from "./RepoIcon";
import { TransitivityIcon } from "./TransitivityIcon";
import { ValidationStateIcon } from "./ValidationStateIcon";

import styles from "./CodeLocationIconList.module.css";

export interface CodeLocationIconListProps {
  repoName?: string;
  repoProvider?: ScmType;
  repoHref?: string;
  branchName?: string;
  branchHref?: string;
  allOtherBranchNames?: string[];
  authorUsername?: string;
  languages?: string[];
  packageName?: string;
  externalTicket?: ExternalTicket | null;
  failedTicket?: TicketAttempt;
  aiTag?: string;
  columnBreakpoint?: MantineSize;
  fontSize?: MantineSize;
  transitivity?: Transitivity;
  reachability?: Reachability;
  reachableCondition?: string;
  validationState?: ValidationState;
  historicalInfo?: HistoricalInfo;
  isGenericSecret?: boolean;
}

// TODO: once secrets uses the issue_parent model, separate this component into several: one for SAST, one for SCA, and one for SECRETS.
/**
 * @returns list of icon and label pairs in a row. Specifically for use with SelectableSeverityCard component
 */
export const CodeLocationIconList: FC<CodeLocationIconListProps> = ({
  repoName,
  repoProvider,
  repoHref,
  branchName,
  branchHref,
  allOtherBranchNames,
  authorUsername,
  languages,
  packageName,
  externalTicket,
  failedTicket,
  aiTag,
  columnBreakpoint = "sm",
  fontSize = "xs",
  transitivity,
  reachability,
  reachableCondition,
  validationState,
  historicalInfo,
  isGenericSecret,
}) => {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  const adjustedFontSize = `calc(${
    theme.fontSizes[fontSize] || theme.fontSizes.xs
  } - 1px)`;

  return (
    <Group
      gap={isMobile ? 6 : "sm"}
      className={styles[`breakpoint-${columnBreakpoint}`]}
      wrap="nowrap"
      align="center"
      fz={adjustedFontSize}
    >
      {/* AI Tag icon */}
      {aiTag && <AiTagIcon aiTag={aiTag} />}

      {/* Transitivity icon */}
      {transitivity && <TransitivityIcon transitivity={transitivity} />}

      {/* Reachability icon */}
      {reachability && (
        <ReachabilityIcon
          reachability={reachability}
          reachableCondition={reachableCondition}
        />
      )}

      {/* Repo icon */}
      {repoName && (
        <RepoIcon
          repoName={repoName}
          repoHref={repoHref}
          repoProvider={repoProvider}
        />
      )}

      {/* Branch icon */}
      {branchName && !historicalInfo && (
        <BranchIcon
          branchName={branchName}
          branchHref={branchHref}
          allOtherBranchNames={allOtherBranchNames}
        />
      )}

      {/* Author icon */}
      {authorUsername && (
        <CodeLocationIcon label={authorUsername} icon={faCircleUser} />
      )}

      {/* Language(s) icon */}
      {languages && (
        <CodeLocationIcon label={languages.join(", ")} icon={faCode} />
      )}

      {/* Package icon */}
      {packageName && <CodeLocationIcon label={packageName} icon={faToolbox} />}

      {/**
       * If generic secrets is shown, validation state shouldn't
       */}
      {isGenericSecret && (
        <CodeLocationIcon label="analyzed by Assistant" icon={faRobot} />
      )}

      {!isGenericSecret && validationState && (
        <ValidationStateIcon validationState={validationState} />
      )}

      {historicalInfo && (
        <CodeLocationIcon
          label={"Historical"}
          icon={faTimelineArrow}
          iconProps={{ flip: "horizontal" }}
        />
      )}

      {/* Ticket icon */}
      {externalTicket ? (
        <CodeLocationIcon
          label={externalTicket.externalSlug}
          href={externalTicket.url}
          icon={faTicket}
        />
      ) : failedTicket ? (
        <CodeLocationIcon
          label="Failed"
          icon={faTicket}
          color="var(--mantine-color-red-7)"
        />
      ) : null}
    </Group>
  );
};
